import { z } from 'zod'

// LLM Config schema
export const llmConfigSchema = z.object({
  provider: z.string().optional(),
  model: z.string().optional(),
  temperature: z.number().optional(),
  max_tokens: z.number().optional(),
  api_key_id: z.string().uuid().optional(),
})

export type LLMConfig = z.infer<typeof llmConfigSchema>

// Memory Config schema
export const memoryConfigSchema = z.object({
  enabled: z.boolean().optional(),
  type: z.string().optional(),
  scope: z.string().optional(),
  context_window: z.number().min(1).max(100).optional(),
  retention_days: z.number().min(1).optional().nullable(),
  auto_summarize: z.boolean().optional(),
  // Legacy fields for backward compatibility
  max_tokens: z.number().optional(),
  window_size: z.number().optional(),
})

export type MemoryConfig = z.infer<typeof memoryConfigSchema>

// Memory types
export const MEMORY_TYPES = [
  { value: 'short_term', label: 'Curto Prazo', description: 'Memória por sessão' },
  { value: 'long_term', label: 'Longo Prazo', description: 'Memória persistente' },
] as const

export type MemoryType = (typeof MEMORY_TYPES)[number]['value']

// Memory scopes
export const MEMORY_SCOPES = [
  { value: 'conversation', label: 'Conversa', description: 'Memória por conversa individual' },
  { value: 'user', label: 'Usuário', description: 'Memória por usuário' },
  { value: 'agent', label: 'Agente', description: 'Memória compartilhada do agente' },
  { value: 'project', label: 'Projeto', description: 'Memória compartilhada do projeto' },
] as const

export type MemoryScope = (typeof MEMORY_SCOPES)[number]['value']

// Agent Settings schema
export const agentSettingsSchema = z.object({
  timeout: z.number().optional(),
  max_retries: z.number().optional(),
  max_iterations: z.number().optional(),
})

export type AgentSettings = z.infer<typeof agentSettingsSchema>

// Agent Tool schema
export const agentToolSchema = z.object({
  id: z.string().uuid(),
  tool_id: z.string().uuid(),
  tool_name: z.string(),
  tool_description: z.string().nullable(),
  tool_type: z.string(),
  config: z.object({}).passthrough().default({}),
  created_at: z.string().datetime(),
})

export type AgentTool = z.infer<typeof agentToolSchema>

// Agent Knowledge Base schema
export const agentKnowledgeBaseSchema = z.object({
  id: z.string().uuid(),
  knowledge_base_id: z.string().uuid(),
  knowledge_base_name: z.string(),
  knowledge_base_description: z.string().nullable(),
  created_at: z.string().datetime(),
})

export type AgentKnowledgeBase = z.infer<typeof agentKnowledgeBaseSchema>

// Agent schema
export const agentSchema = z.object({
  id: z.string().uuid(),
  project_id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  system_prompt: z.string().nullable(),
  llm_config: llmConfigSchema.default({}),
  memory_config: memoryConfigSchema.default({}),
  settings: agentSettingsSchema.default({}),
  is_template: z.boolean(),
  is_active: z.boolean(),
  tools: z.array(agentToolSchema).default([]),
  knowledge_bases: z.array(agentKnowledgeBaseSchema).default([]),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
})

export type Agent = z.infer<typeof agentSchema>

// Agent List Response schema
export const agentListResponseSchema = z.object({
  agents: z.array(agentSchema),
  total: z.number(),
  page: z.number(),
  page_size: z.number(),
  total_pages: z.number(),
})

export type AgentListResponse = z.infer<typeof agentListResponseSchema>

// Query params for listing agents
export interface AgentListParams {
  search?: string
  is_active?: boolean
  is_template?: boolean
  page?: number
  page_size?: number
}
